<template>
    <Sheet v-loading="loading.global">
        <div style="margin-bottom: 10px;">
            <el-divider>
            <span style="font-size:1.1rem;padding-right: 10px;">
                {{lastDrewMonth ? `${lastDrewMonth.month}月份` : ''}} 夜間場地借用抽籤結果查詢
            </span>
            </el-divider>
            <div v-if="lastDrewMonth">
                <span style="font-size:1.1rem;padding-right: 10px;">選擇場地:</span>
                <el-select v-model="roomId" size="large" @change="changeRoom">
                    <el-option
                            v-for="room in drawableRooms"
                            :key="room.id"
                            :value="room.id"
                            :label="room.name"
                    ></el-option>
                </el-select>
            </div>
        </div>
        <div style="display: flex; flex-direction: column; flex-grow: 1;height: 620px;"
             id="drewResult"
             v-if="lastDrewMonth"
        >
            <template v-if="drew.length > 0">
                <calendar-view
                        locale="zh-Tw"
                        :show-date="showDate"
                        class="theme-default"
                        @click-event="onClickEvent"
                        :events="drew"
                        :startingDayOfWeek="1"
                        eventContentHeight="1.5em"
                >
                </calendar-view>
                <div
                        v-if="activeEventTooltip.visible"
                        ref="eventTooltip"
                        class="draw-result-tooltip"
                        :class="`draw-result-tooltip--${activeEventTooltip.placement}`"
                        :style="activeEventTooltipStyle"
                        role="tooltip"
                        aria-live="polite"
                >
                    {{activeEventTooltip.title}}
                </div>
            </template>
            <div v-else style="text-align: center">
                <h1>抱歉，此場地尚未抽籤</h1>
            </div>
        </div>
        <div v-else style="text-align: center">
            <h1>抱歉，尚未開始抽籤</h1>
        </div>
    </Sheet>
</template>

<script>
    import {CalendarView, CalendarViewHeader} from 'vue-simple-calendar';
    require('vue-simple-calendar/static/css/default.css');
    import {mapState, mapActions} from 'vuex';
    import moment from 'moment';

    const tooltipMediaQuery = '(hover: none), (pointer: coarse)';

    export default {
        name: 'DrawResult',
        data: () => ({
            roomId: '',
            isTapTooltipContext: false,
            activeEventTooltip: {
                visible: false,
                eventId: undefined,
                title: '',
                top: 0,
                left: 0,
                maxWidth: 280,
                placement: 'bottom'
            },
            activeEventAnchorRect: undefined,
            pointerMedia: undefined,
        }),
        components: {
            CalendarView,
            CalendarViewHeader,
        },
        computed:{
            ...mapState('setting', {
                rooms: state => state.rooms,
                months: state => state.months,
                ncutRooms: state => state.ncutRooms
            }),
            ...mapState(['loading']),
            ...mapState('booking', ['drew']),
            drawableRooms() {
                const drawableNcutRoomIds = new Set(this.rooms.filter(r => r.drawable)
                    .map(r => r.ncutRoomId));
                return this.ncutRooms.filter(r => drawableNcutRoomIds.has(r.id));
            },
            lastDrewMonth() {
                return [...this.months]
                    .sort((a, b) => parseInt(moment(b.fstDate).year() + '' + (b.month + '').padStart(2, '0'))
                        - parseInt(moment(a.fstDate).year() + '' + (a.month + '').padStart(2, '0')))
                    .find(m => m.drewRoomIds.length > 0);
            },
            showDate(){
                if(this.lastDrewMonth){
                    const {fstDate, month} = this.lastDrewMonth;
                    return new Date(moment(fstDate).year(), month - 1, 1 );
                }
                return new Date();
            },
            activeEventTooltipStyle() {
                return {
                    top: `${this.activeEventTooltip.top}px`,
                    left: `${this.activeEventTooltip.left}px`,
                    maxWidth: `${this.activeEventTooltip.maxWidth}px`
                };
            }
        },
        methods:{
            ...mapActions('setting', {
                getNcutRooms: 'getNcutRoomsAction',
                getMonths: 'getMonthsAction',
            }),
            ...mapActions('booking', {
                getDrewEvents: 'getDrewEventsAction'
            }),
            async changeRoom(roomId){
                this.hideEventTooltip();
                const {month} = this.lastDrewMonth;
                await this.getDrewEvents({roomId, month})
            },
            updateTapTooltipContext() {
                if (typeof window === 'undefined') {
                    return;
                }
                const useTapTooltip = window.innerWidth <= 768
                    || window.matchMedia(tooltipMediaQuery).matches;
                this.isTapTooltipContext = useTapTooltip;
            },
            onClickEvent(calendarItem, windowEvent) {
                if (!this.isTapTooltipContext) {
                    return;
                }

                const title = this.getCalendarItemTitle(calendarItem);
                if (!title) {
                    return;
                }

                if (this.activeEventTooltip.visible && this.activeEventTooltip.eventId === calendarItem.id) {
                    this.hideEventTooltip();
                    return;
                }

                const target = (windowEvent && windowEvent.currentTarget) || (windowEvent && windowEvent.target);
                if (!target || typeof target.getBoundingClientRect !== 'function') {
                    return;
                }

                const {innerWidth = 320} = window;
                const maxWidth = Math.min(320, Math.max(180, innerWidth - 24));
                const anchorRect = target.getBoundingClientRect();

                this.activeEventAnchorRect = {
                    top: anchorRect.top,
                    bottom: anchorRect.bottom,
                    left: anchorRect.left,
                    width: anchorRect.width,
                };
                this.activeEventTooltip = {
                    visible: true,
                    eventId: calendarItem.id,
                    title,
                    top: anchorRect.bottom + 8,
                    left: Math.max(12, anchorRect.left),
                    maxWidth,
                    placement: 'bottom'
                };

                this.$nextTick(() => this.adjustTooltipPosition());
            },
            adjustTooltipPosition() {
                if (!this.activeEventTooltip.visible || !this.activeEventAnchorRect || typeof window === 'undefined') {
                    return;
                }

                const tooltip = Array.isArray(this.$refs.eventTooltip)
                    ? this.$refs.eventTooltip[0]
                    : this.$refs.eventTooltip;
                if (!tooltip) {
                    return;
                }

                const margin = 12;
                const gap = 8;
                const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
                const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
                const tooltipRect = tooltip.getBoundingClientRect();
                const anchorRect = this.activeEventAnchorRect;

                let left = anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2;
                left = Math.min(Math.max(margin, left), Math.max(margin, viewportWidth - tooltipRect.width - margin));

                let placement = 'bottom';
                let top = anchorRect.bottom + gap;
                if (top + tooltipRect.height > viewportHeight - margin && anchorRect.top - gap - tooltipRect.height >= margin) {
                    placement = 'top';
                    top = anchorRect.top - gap - tooltipRect.height;
                }

                if (placement === 'bottom' && top + tooltipRect.height > viewportHeight - margin) {
                    top = Math.max(margin, viewportHeight - tooltipRect.height - margin);
                }
                if (placement === 'top' && top < margin) {
                    top = margin;
                }

                this.activeEventTooltip = {
                    ...this.activeEventTooltip,
                    top,
                    left,
                    placement
                };
            },
            hideEventTooltip() {
                this.activeEventAnchorRect = undefined;
                this.activeEventTooltip = {
                    ...this.activeEventTooltip,
                    visible: false,
                    eventId: undefined,
                    title: ''
                };
            },
            getCalendarItemTitle(calendarItem) {
                return calendarItem.title || ((calendarItem.originalEvent || {}).title) || '';
            },
            handleViewportChange() {
                this.updateTapTooltipContext();
                this.hideEventTooltip();
            },
            handleDocumentClick(event) {
                if (!this.activeEventTooltip.visible) {
                    return;
                }

                const target = event.target;
                if (this.isWithin(target, '.draw-result-tooltip') || this.isWithin(target, '.cv-event')) {
                    return;
                }

                this.hideEventTooltip();
            },
            handleKeydown(event) {
                if (event.key === 'Escape') {
                    this.hideEventTooltip();
                }
            },
            isWithin(target, selector) {
                return !!(target && typeof target.closest === 'function' && target.closest(selector));
            }
        },
        watch:{
            async drawableRooms(current) {
                this.hideEventTooltip();
                if (current && current[0]) {
                    this.roomId = current[0].id;
                    const {roomId} = this;
                    const {month} = this.lastDrewMonth || {month: 0};
                    await this.getDrewEvents({roomId, month})
                }
            },
            drew() {
                this.hideEventTooltip();
            }
        },
        mounted() {
            this.updateTapTooltipContext();

            if (typeof window !== 'undefined') {
                window.addEventListener('resize', this.handleViewportChange);
                window.addEventListener('orientationchange', this.handleViewportChange);
                window.addEventListener('keydown', this.handleKeydown);
                this.pointerMedia = window.matchMedia(tooltipMediaQuery);
                if (this.pointerMedia.addEventListener) {
                    this.pointerMedia.addEventListener('change', this.handleViewportChange);
                } else if (this.pointerMedia.addListener) {
                    this.pointerMedia.addListener(this.handleViewportChange);
                }
            }

            if (typeof document !== 'undefined') {
                document.addEventListener('click', this.handleDocumentClick, true);
            }
        },
        beforeDestroy() {
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', this.handleViewportChange);
                window.removeEventListener('orientationchange', this.handleViewportChange);
                window.removeEventListener('keydown', this.handleKeydown);
                if (this.pointerMedia) {
                    if (this.pointerMedia.removeEventListener) {
                        this.pointerMedia.removeEventListener('change', this.handleViewportChange);
                    } else if (this.pointerMedia.removeListener) {
                        this.pointerMedia.removeListener(this.handleViewportChange);
                    }
                }
            }

            if (typeof document !== 'undefined') {
                document.removeEventListener('click', this.handleDocumentClick, true);
            }
        },
        async created() {
            await this.getNcutRooms();
        }
    }
</script>

<style>
    #drewResult .cv-event{
        margin-bottom: 3px;
        cursor: pointer;
    }

    .draw-result-tooltip {
        position: fixed;
        z-index: 3000;
        padding: 10px 12px;
        border-radius: 10px;
        background: rgba(48, 49, 51, 0.96);
        color: #fff;
        line-height: 1.5;
        font-size: 0.9rem;
        box-shadow: 0 10px 28px rgba(15, 23, 42, 0.24);
        word-break: break-word;
    }

    .draw-result-tooltip::after {
        content: '';
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
    }

    .draw-result-tooltip--bottom::after {
        top: -12px;
        border-bottom-color: rgba(48, 49, 51, 0.96);
    }

    .draw-result-tooltip--top::after {
        bottom: -12px;
        border-top-color: rgba(48, 49, 51, 0.96);
    }

    @media (min-width: 769px) and (hover: hover) {
        .draw-result-tooltip {
            display: none;
        }
    }
</style>